import CommonUtils from 'utils/CommonUtils';
import Event from 'domain/Event';

const get = (locationId) => {
    return {
        type: 'GET_LOCATION',
        payload: (async () => {
            const query = new Parse.Query('Location');
            query.include(['parent']);
            const location = await query.get(locationId);
            
            const eventQuery = new Parse.Query('EventLocation');
            eventQuery.equalTo('location', location);
            eventQuery.include(['event', 'event.type']);
            const locationEvents = await eventQuery.find();
            
            location.set('events', locationEvents.map(le => new Event(le.get('event'))));
            return location;
        })()
    };
};

const list = ({ page = 1, pageSize = 20, loadMore = false, ...filters } = {}) => {
    return {
        type: loadMore ? 'LOAD_MORE_LOCATION' : 'LIST_LOCATION',
        payload: (async () => {
            const query = new Parse.Query('Location');
            query.include(['parent']);
            query.ascending('label');
            
            if (filters.parent) {
                query.equalTo('parent', filters.parent);
            }
            if (filters.label) {
                query.contains('label', filters.label);
            }
            if (filters.coordinates) {
            }
            
            const skip = (page - 1) * pageSize;
            query.skip(skip);
            query.limit(pageSize + 1);
            
            const results = await query.find();
            const hasMore = results.length > pageSize;
            const data = hasMore ? results.slice(0, pageSize) : results;
            
            return {
                data,
                pagination: {
                    page,
                    pageSize,
                    hasMore,
                    total: data.length
                }
            };
        })()
    };
};

const save = (locationData) => {
    return {
        type: 'SAVE_LOCATION',
        payload: (async () => {
            let location;
            
            if (locationData.objectId) {
                const query = new Parse.Query('Location');
                location = await query.get(locationData.objectId);
            } else {
                location = new Parse.Object('Location');
            }
            
            const parentId = locationData.parent?.objectId || locationData.parent?.id;
            location.set('label', locationData.label);
            location.set('parent', parentId ? CommonUtils.createPointer('Location', parentId) : null);
            location.set('isCentroid', locationData.isCentroid || false);
            if (locationData.coordinates && locationData.coordinates.latitude != null && locationData.coordinates.longitude != null) {
                location.set('coordinates', new Parse.GeoPoint({
                    latitude: Math.round(locationData.coordinates.latitude * 10000) / 10000,
                    longitude: Math.round(locationData.coordinates.longitude * 10000) / 10000
                }));
            } else {
                location.unset('coordinates');
            }
            
            const savedLocation = await location.save();
            
            if (locationData.events && Array.isArray(locationData.events)) {
                const existingEventQuery = new Parse.Query('EventLocation');
                existingEventQuery.equalTo('location', savedLocation);
                const existingEvents = await existingEventQuery.find();
                
                if (existingEvents.length > 0) {
                    await Parse.Object.destroyAll(existingEvents);
                }
                
                const newEventLocations = locationData.events.map(eventId => {
                    const eventLocation = new Parse.Object('EventLocation');
                    eventLocation.set('location', savedLocation);
                    eventLocation.set('event', CommonUtils.createPointer('Event', eventId));
                    return eventLocation;
                });
                
                if (newEventLocations.length > 0) {
                    await Parse.Object.saveAll(newEventLocations);
                }
            }
            
            return savedLocation;
        })()
    };
};

const update = (locationId, locationData) => {
    return {
        type: 'UPDATE_LOCATION',
        payload: (async () => {
            const location = new Parse.Object('Location');
            location.id = locationId;
            
            if (locationData.label !== undefined) location.set('label', locationData.label);
            if (locationData.parent !== undefined) {
                const parentId = locationData.parent?.objectId || locationData.parent?.id;
                location.set('parent', parentId ? CommonUtils.createPointer('Location', parentId) : null);
            }
            if (locationData.isCentroid !== undefined) location.set('isCentroid', locationData.isCentroid);
            if (locationData.coordinates !== undefined) {
                if (locationData.coordinates && locationData.coordinates.latitude != null && locationData.coordinates.longitude != null) {
                    location.set('coordinates', new Parse.GeoPoint({
                        latitude: Math.round(locationData.coordinates.latitude * 10000) / 10000,
                        longitude: Math.round(locationData.coordinates.longitude * 10000) / 10000
                    }));
                } else {
                    location.unset('coordinates');
                }
            }

            return await location.save();
        })()
    };
};

const remove = (locationId) => {
    return {
        type: 'DELETE_LOCATION',
        payload: (async () => {
            const locationPointer = CommonUtils.createPointer('Location', locationId);
            
            const childQuery = new Parse.Query('Location');
            childQuery.equalTo('parent', locationPointer);
            const childCount = await childQuery.count();
            
            if (childCount > 0) {
                throw new Error(`Cannot delete location: ${childCount} child locations exist`);
            }
            
            const observationQuery = new Parse.Query('Observation');
            observationQuery.equalTo('location', locationPointer);
            const observationCount = await observationQuery.count();
            
            if (observationCount > 0) {
                throw new Error(`Cannot delete location: ${observationCount} observations reference this location`);
            }
            
            const noteQuery = new Parse.Query('Note');
            noteQuery.equalTo('location', locationPointer);
            const notes = await noteQuery.find();
            
            if (notes.length > 0) {
                await Parse.Object.destroyAll(notes);
            }
            
            const location = new Parse.Object('Location');
            location.id = locationId;
            await location.destroy();
            
            return { 
                id: locationId,
                deletedNotesCount: notes.length
            };
        })()
    };
};

const clear = () => {
    return {
        type: 'CLEAR_LOCATION'
    };
};

const setCurrent = (location) => {
    return {
        type: 'SET_CURRENT_LOCATION',
        payload: location
    };
};

export default {
    get,
    list,
    save,
    update,
    remove,
    clear,
    setCurrent
};